import type { JSX, ReactNode } from 'react'

export interface EmptyStateProps {
  'data-testid': string
  title: ReactNode
  description?: ReactNode
  /** The one action out of the empty state, usually a Button. */
  action?: ReactNode
}

/**
 * EmptyState — states the gap and offers the one way out of it.
 *
 * One action, not several: an empty state that offers three choices is a menu,
 * and a menu is not what someone who just hit a dead end needs.
 *
 * @param props - EmptyState props.
 * @param props.title - What is missing.
 * @param props.description - Optional detail.
 * @param props.action - Optional single action.
 * @returns The rendered empty state.
 */
export function EmptyState({
  'data-testid': testId,
  title,
  description,
  action
}: EmptyStateProps): JSX.Element {
  return (
    <div
      data-testid={testId}
      className="grid place-items-center gap-2 rounded-lg border border-dashed p-12 text-center"
    >
      <p className="text-xl font-semibold">{title}</p>
      {description ? (
        <p className="text-muted-foreground max-w-prose text-sm">
          {description}
        </p>
      ) : null}
      {action}
    </div>
  )
}
